import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, CheckCircle2, Circle, MessageSquare, Radio, Server } from "lucide-react";
import { PageHeader, Card } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { PurgeCountdown } from "@/components/ranger/PurgeCountdown";
import { OnboardingGuide } from "@/components/ranger/OnboardingGuide";
import { useAuth } from "@/context/AuthContext";
import { api, fmtTime } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div>
    <div className="rg-label">{label}</div>
    <div data-testid={testId} className="mt-1 font-display text-3xl font-bold text-slate-100">{value ?? "—"}</div>
  </div>
);

const Checklist = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map(([done, text]) => (
      <li key={text} className="flex items-center gap-3 text-sm">
        {done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-slate-600" />}
        <span className={done ? "text-slate-300" : "text-slate-500"}>{text}</span>
      </li>
    ))}
  </ul>
);

const ServerInfo = ({ info }) => {
  if (!info) return <p className="text-sm text-slate-500">No serverinfo received yet. Data appears after the first successful RCON health check.</p>;
  const entries = Object.entries(info.data).slice(0, 12);
  return (
    <div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
        {entries.map(([k, v]) => (
          <div key={k}><dt className="rg-label">{k}</dt><dd className="mt-0.5 truncate font-mono text-sm text-slate-200">{String(v)}</dd></div>
        ))}
      </dl>
      <p className="mt-4 font-mono text-[10px] text-slate-600">Fetched {fmtTime(info.fetched_at)}</p>
    </div>
  );
};

export default function Overview() {
  const { active } = useAuth();
  const [c, setC] = useState(null);
  const [health, setHealth] = useState(null);
  const [bot, setBot] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get(`/communities/${active.id}`).then((r) => setC(r.data));
      api.get(`/communities/${active.id}/health/history?hours=24`).then((r) => setHealth(r.data.summary));
      api.get("/discord/status").then((r) => setBot(r.data));
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [active.id]);

  if (!c) return <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Loading telemetry…</div>;
  const rs = c.rcon_status;

  return (
    <div>
      <PageHeader eyebrow={`${c.mode === "pvp" ? "24/7 PvP" : "PvE · Monthly purge"} · ${c.platforms.join(" / ")}`} title="Overview" description={c.description} />
      <OnboardingGuide cid={c.id} />
      {rs.state === "unconfigured" && (
        <div data-testid="rcon-unconfigured-banner" className="mb-6 flex flex-col gap-3 rounded-sm border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200 sm:flex-row sm:items-center sm:justify-between">
          <span>RCON is not configured. No live server data is shown until a WebRCON connection is established.</span>
          <Link data-testid="configure-rcon-link" to="/servers" className="font-semibold uppercase tracking-wider text-amber-300 hover:underline">Configure →</Link>
        </div>
      )}
      <PurgeCountdown cid={c.id} />
      <div className="grid gap-5 md:grid-cols-3">
        <Card title="WebRCON" icon={Radio} right={<StatusBadge state={rs.state} testId="overview-rcon-status" />}>
          <p className="font-mono text-xs text-slate-400">{c.rcon.configured ? `${c.rcon.host}:${c.rcon.port}` : "No host set"}</p>
          {rs.error && <p data-testid="overview-rcon-error" className="mt-2 break-words font-mono text-xs text-red-400">{rs.error}</p>}
          <p className="mt-3 text-xs text-slate-500">Connected since: {fmtTime(rs.connected_at)}</p>
        </Card>
        <Card title="Discord Bot" icon={MessageSquare} right={bot && <StatusBadge state={bot.state} testId="overview-bot-status" />}>
          <p className="text-sm text-slate-400">{bot?.bot_user || "No bot session"}</p>
          {bot?.error && <p className="mt-2 font-mono text-xs text-red-400">{bot.error}</p>}
          <p className="mt-3 text-xs text-slate-500">Guild: {c.discord.guild_id || "not set"}</p>
        </Card>
        <Card title="Health · 24h" icon={Activity}>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Uptime" value={health?.uptime_pct != null ? `${health.uptime_pct}%` : null} testId="overview-uptime" />
            <Stat label="Avg latency" value={health?.avg_latency_ms != null ? `${health.avg_latency_ms}ms` : null} testId="overview-latency" />
          </div>
          <p className="mt-3 text-xs text-slate-500">{health?.total ? `${health.total} checks recorded` : "No health checks recorded yet"}</p>
        </Card>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-[2fr_1fr]">
        <Card title="Live server info" icon={Server} testId="overview-serverinfo"><ServerInfo info={c.last_serverinfo} /></Card>
        <Card title="Setup checklist" testId="overview-checklist">
          <Checklist items={[
            [c.rcon.configured, "WebRCON host, port & password saved"],
            [rs.state === "connected", "WebRCON connection live"],
            [!!bot?.oauth_configured, "Discord OAuth2 credentials set"],
            [bot?.state === "ready", "Discord bot online"],
            [!!c.discord.guild_id, "Discord guild linked"],
            [!!c.discord.notify_channel_id, "Alert channel configured"],
          ]} />
        </Card>
      </div>
    </div>
  );
}
